import type { ReactNode } from 'react';

import { Spinner } from 'components/ui';
import { useAccountContext } from 'utils/account/AccountContext';
import { isE2ETest, isLocal, isTest } from 'utils/env';
import { CUSTOMIZABLE_DASHBOARD } from 'utils/release-toggles';

import { CustomizableDashboard } from './CustomizableDashboard';

type DashboardLandingProps = {
  /**
   * What to render when the customizable dashboard is NOT enabled — the legacy
   * Home page for normal users, or a redirect for work-order collaborators.
   */
  fallback: ReactNode;
};

/**
 * Toggle gate for the "/" landing route. Renders the customizable dashboard when
 * the `customizable_dashboard` release toggle is on (or on localhost, for dev
 * preview); otherwise renders the provided fallback unchanged.
 *
 * All feature branching lives here, inside the feature module, so the shared
 * `Home` component and `App.jsx` routing stay free of dashboard knowledge — when
 * the toggle is off, behaviour is byte-for-byte identical to today.
 *
 * The dev-preview `isLocal` shortcut is suppressed in test environments — under
 * Playwright (`isE2ETest`) so the existing mocked Home/Dashboard e2e suite keeps
 * exercising the legacy page, and under unit tests (`isTest`) where jsdom also runs
 * on localhost (rendering App at "/" must keep yielding the fallback). Test coverage
 * of this feature opts in explicitly via the release toggle.
 */
export function DashboardLanding({ fallback }: DashboardLandingProps) {
  const { customer, hasReleaseToggle, loading, featuresLoading } = useAccountContext();

  // `hasReleaseToggle` reads a jotai atom that AccountContextProvider only
  // populates in a post-mount effect, so on the first render after a page load it
  // can report every toggle as off. Acting on that here is harmful: the work-order
  // landing fallback is a <Redirect>, which navigates away synchronously and never
  // returns once the toggle resolves. The toggle atom is guaranteed populated once
  // the feature-flags query has settled, so wait for that (and the customer) before
  // choosing between the dashboard and the fallback.
  if ((!customer && loading) || featuresLoading) {
    return <Spinner />;
  }

  const devPreview = isLocal && !isE2ETest && !isTest;
  if (hasReleaseToggle(CUSTOMIZABLE_DASHBOARD) || devPreview) {
    return <CustomizableDashboard />;
  }

  return <>{fallback}</>;
}
